import React, { useState, useCallback, useEffect, useRef, useMemo, memo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Radio, Video, VideoOff, Mic, MicOff, MonitorUp,
  Loader2, AlertCircle, Settings, Clock, Volume2,
  Activity, Wifi, HardDrive, CheckCircle, Square, Flag, Music,
  Users, Play, Pause, ArrowRight, AlertTriangle, Info, XCircle,
  MessageSquare, Heart, Gift, UserPlus, Bell, Zap, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API = window.__BACKEND_URL;

// ── Audio singleton ──
const _audioCache = {};
function playAudio(url) {
  if (_audioCache[url]) { _audioCache[url].currentTime = 0; _audioCache[url].play().catch(() => {}); return _audioCache[url]; }
  const a = new Audio(url); a.volume = 0.7; _audioCache[url] = a; a.play().catch(() => {}); return a;
}
function stopAudio(url) { if (_audioCache[url]) { _audioCache[url].pause(); _audioCache[url].currentTime = 0; } }
function getSoundUrl(sound) { return sound?.url || (sound?.proxy_url ? `${API}${sound.proxy_url}` : null); }

// ── Perf: isolated timer ──
const LiveTimer = memo(function LiveTimer({ startTime }) {
  const [display, setDisplay] = useState('00:00:00');
  useEffect(() => {
    const iv = setInterval(() => {
      const d = Date.now() - startTime;
      setDisplay(`${String(Math.floor(d/3600000)).padStart(2,'0')}:${String(Math.floor((d%3600000)/60000)).padStart(2,'0')}:${String(Math.floor((d%60000)/1000)).padStart(2,'0')}`);
    }, 1000);
    return () => clearInterval(iv);
  }, [startTime]);
  return <span className="font-mono">{display}</span>;
});

// ── Platform icon helper ──
const PLAT_ICONS = { youtube: '/', facebook: 'f', tiktok: 't', instagram: 'ig' };
const PLAT_COLORS = { youtube: 'text-red-400 bg-red-500/10 border-red-500/30', facebook: 'text-blue-400 bg-blue-500/10 border-blue-500/30', tiktok: 'text-pink-400 bg-pink-500/10 border-pink-500/30', instagram: 'text-purple-400 bg-purple-500/10 border-purple-500/30' };
function PlatBadge({ platform }) {
  const c = PLAT_COLORS[platform] || 'text-[#8A93A6] bg-white/5 border-white/10';
  return <Badge className={`${c} text-[10px] capitalize border px-1.5 py-0`}>{platform}</Badge>;
}

// ── Chat event icons ──
const EVT_ICONS = { message: MessageSquare, like: Heart, gift: Gift, follow: UserPlus, comment: MessageSquare };

// ─── Pre-Live Setup ───────────────────────────────────────
function PreLiveSetup({ onGoLive, loading, soundCatalog, creatorId, projectId }) {
  const [roomName, setRoomName] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [previewRequesting, setPreviewRequesting] = useState(false);
  const previewRef = useRef(null);
  const streamRef = useRef(null);

  // Multistream platforms — no connected-platforms fetch is made here to
  // avoid introducing a new backend call just to populate demo UI; this
  // accurately reflects "no platforms connected" until Level 3 wires it
  // to the real Creator Profile connections.
  const connectedPlatforms = [];
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  // Sound configuration
  const alertEvents = useMemo(() => [
    { type: 'likes', label: 'Likes', icon: Heart },
    { type: 'comments', label: 'Comments', icon: MessageSquare },
    { type: 'gifts', label: 'Gifts', icon: Gift },
    { type: 'follows', label: 'Follows / Subs', icon: UserPlus },
  ], []);
  const [alertMappings, setAlertMappings] = useState(() => alertEvents.map(e => ({ event_type: e.type, sound_name: '', sound_url: '', enabled: true })));
  const [selectedIntro, setSelectedIntro] = useState(null);
  const [selectedOutro, setSelectedOutro] = useState(null);
  const [sfxLoadout, setSfxLoadout] = useState([]);

  const allAlerts = soundCatalog?.alert_sounds || [];
  const allIntros = soundCatalog?.intro_tracks || [];
  const allOutros = soundCatalog?.outro_tracks || [];
  const allSfx = soundCatalog?.sound_effects || [];
  const b2Ok = soundCatalog?.configured !== false;

  useEffect(() => {
    // Enumerate devices only — this does not itself prompt for camera/mic
    // permission, so it is safe to run on mount. Labels may be generic
    // ("Camera 1") until the user explicitly enables the preview below.
    const enumerate = async () => {
      try {
        const dl = await navigator.mediaDevices.enumerateDevices();
        // Pre-permission, some browsers report unlabeled devices with an
        // empty deviceId — exclude those, they are not a selectable value.
        const cams = dl.filter(d => d.kind === 'videoinput' && d.deviceId);
        const mics = dl.filter(d => d.kind === 'audioinput' && d.deviceId);
        setDevices({ cameras: cams, mics });
        if (cams[0]) setSelectedCamera(cams[0].deviceId);
        if (mics[0]) setSelectedMic(mics[0].deviceId);
      } catch { /* enumeration unsupported — selectors stay empty */ }
      finally { setDevicesLoading(false); }
    };
    enumerate();
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  // Explicit, user-initiated camera/mic permission request — never
  // requested automatically on page load.
  const enablePreview = async () => {
    setPreviewRequesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (previewRef.current) previewRef.current.srcObject = stream;
      const dl = await navigator.mediaDevices.enumerateDevices();
      setDevices({ cameras: dl.filter(d => d.kind === 'videoinput' && d.deviceId), mics: dl.filter(d => d.kind === 'audioinput' && d.deviceId) });
      setPreviewEnabled(true);
    } catch { toast.error('Camera/mic access denied'); }
    finally { setPreviewRequesting(false); }
  };

  const togglePlatform = (p) => setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const updateAlert = (idx, field, val) => setAlertMappings(prev => prev.map((a, i) => i === idx ? { ...a, [field]: val } : a));
  const toggleSfx = (s) => setSfxLoadout(prev => prev.find(x => x.name === s.name) ? prev.filter(x => x.name !== s.name) : [...prev, s]);

  // Go Live Presets (section 8 audit) — only sound/alert choices and
  // preferred platforms are reusable; camera, mic and room name are
  // per-machine/session and are intentionally never captured here, and
  // no credential or live session token is ever touched.
  const buildGoLivePresetPayload = () => ({ golive: { alertMappings, intro: selectedIntro, outro: selectedOutro, sfxLoadout, preferredPlatforms: selectedPlatforms } });
  const applyGoLivePreset = (tpl, editTpl) => {
    if (!editTpl.golive) { toast.error('This preset has no saved setup.'); return; }
    setAlertMappings(editTpl.golive.alertMappings || alertMappings);
    setSelectedIntro(editTpl.golive.intro || null);
    setSelectedOutro(editTpl.golive.outro || null);
    setSfxLoadout(editTpl.golive.sfxLoadout || []);
    setSelectedPlatforms((editTpl.golive.preferredPlatforms || []).filter((p) => connectedPlatforms.includes(p)));
    toast.success(`Applied "${editTpl.name}" — sounds and platform picks loaded. Camera, mic and room name are left as they are.`);
  };
  const defaultRoom = `${(creatorId || 'session').toString().toLowerCase()}-${Date.now().toString(36)}`;

  const handleGoLive = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    onGoLive(roomName || defaultRoom, selectedPlatforms, {
      alert_mappings: alertMappings,
      intro: selectedIntro,
      outro: selectedOutro,
      sfx_loadout: sfxLoadout,
    });
  };

  return (
    <div className="space-y-6" data-testid="pre-live-setup">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera preview */}
        <Card className="glass">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Video className="h-4 w-4 text-[#3A7BFF]" />Camera Preview</CardTitle></CardHeader>
          <CardContent>
            <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
              <video ref={previewRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              {!previewEnabled && !devicesLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
                  <Button onClick={enablePreview} disabled={previewRequesting} className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" data-testid="enable-preview-btn">
                    {previewRequesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
                    Enable Camera Preview
                  </Button>
                  <p className="text-xs text-[#8A93A6] text-center px-6">Your browser will ask for camera/mic permission — nothing is requested automatically.</p>
                </div>
              )}
              {devicesLoading && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-8 w-8 text-white animate-spin" /></div>}
            </div>
          </CardContent>
        </Card>

        {/* Session setup + Platform selection */}
        <Card className="glass">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4 text-[#3A7BFF]" />Session Setup</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#8A93A6]">Room Name</Label>
              <Input value={roomName} onChange={e => setRoomName(e.target.value)} placeholder={defaultRoom} className="bg-[#121417] border-white/10 mt-1" data-testid="room-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#8A93A6]">Camera</Label>
                <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                  <SelectTrigger className="bg-[#121417] border-white/10 mt-1" data-testid="camera-select"><SelectValue placeholder="Camera" /></SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-white/10">{devices.cameras.map(d => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8A93A6]">Microphone</Label>
                <Select value={selectedMic} onValueChange={setSelectedMic}>
                  <SelectTrigger className="bg-[#121417] border-white/10 mt-1" data-testid="mic-select"><SelectValue placeholder="Mic" /></SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-white/10">{devices.mics.map(d => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || 'Mic'}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Multistream destinations */}
            <div>
              <Label className="text-[#8A93A6]">Stream To</Label>
              {connectedPlatforms.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="platform-selection">
                  {connectedPlatforms.map(p => {
                    const name = p.platform || p;
                    const checked = selectedPlatforms.includes(name);
                    return (
                      <label key={name} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${checked ? `${PLAT_COLORS[name] || 'border-[#3A7BFF] bg-[#3A7BFF]/10'}` : 'border-white/10 hover:bg-white/5'}`}>
                        <Checkbox checked={checked} onCheckedChange={() => togglePlatform(name)} />
                        <span className="capitalize text-sm text-white">{name}</span>
                        {p.account_name && <span className="text-xs text-[#8A93A6]">({p.account_name})</span>}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-yellow-400 mt-1">No platforms connected. Connect platforms in Creator Profile to enable multistreaming.</p>
              )}
            </div>

            <Button onClick={handleGoLive} disabled={loading} className="w-full bg-red-600 hover:bg-red-700 h-12 text-base" data-testid="go-live-btn">
              {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Radio className="h-5 w-5 mr-2" />}
              Go Live{selectedPlatforms.length > 0 ? ` to ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''}` : ''}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Sound Configuration */}
      <Card className="glass">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Volume2 className="h-4 w-4 text-[#3A7BFF]" />Sound Configuration</CardTitle></CardHeader>
        <CardContent>
          {!b2Ok ? (
            <p className="text-xs text-[#8A93A6] py-4 text-center">Sound assets load from B2 storage. Connect storage to configure sounds.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="sound-config">
              {/* Alert Mappings */}
              <div>
                <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Alert Sounds (Auto-triggered)</Label>
                <div className="space-y-2 mt-2">
                  {alertEvents.map((evt, idx) => (
                    <div key={evt.type} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/5">
                      <Checkbox checked={alertMappings[idx]?.enabled} onCheckedChange={v => updateAlert(idx, 'enabled', v)} />
                      <evt.icon className="h-4 w-4 text-[#8A93A6] flex-shrink-0" />
                      <span className="text-sm text-white w-20">{evt.label}</span>
                      <Select value={alertMappings[idx]?.sound_name || ''} onValueChange={v => {
                        const s = allAlerts.find(a => a.name === v);
                        updateAlert(idx, 'sound_name', v);
                        updateAlert(idx, 'sound_url', getSoundUrl(s) || '');
                      }}>
                        <SelectTrigger className="bg-[#121417] border-white/10 h-8 text-xs flex-1"><SelectValue placeholder="Select sound" /></SelectTrigger>
                        <SelectContent className="bg-[#1C1F26] border-white/10">{allAlerts.map(s => <SelectItem key={s.name} value={s.name}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {/* Intro */}
                <div>
                  <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Intro Track</Label>
                  <div className="flex gap-2 mt-2">
                    {allIntros.map(s => (
                      <Button key={s.name} size="sm" variant={selectedIntro?.name === s.name ? 'default' : 'outline'} onClick={() => setSelectedIntro(selectedIntro?.name === s.name ? null : s)} className={`text-xs ${selectedIntro?.name === s.name ? 'bg-purple-600' : 'border-white/10'}`} data-testid={`intro-${s.name}`}>
                        <Music className="h-3 w-3 mr-1" />{s.label}
                      </Button>
                    ))}
                    {allIntros.length === 0 && <p className="text-xs text-[#8A93A6]">No intro tracks available</p>}
                  </div>
                </div>
                {/* Outro */}
                <div>
                  <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Outro Track</Label>
                  <div className="flex gap-2 mt-2">
                    {allOutros.map(s => (
                      <Button key={s.name} size="sm" variant={selectedOutro?.name === s.name ? 'default' : 'outline'} onClick={() => setSelectedOutro(selectedOutro?.name === s.name ? null : s)} className={`text-xs ${selectedOutro?.name === s.name ? 'bg-pink-600' : 'border-white/10'}`} data-testid={`outro-${s.name}`}>
                        <Music className="h-3 w-3 mr-1" />{s.label}
                      </Button>
                    ))}
                    {allOutros.length === 0 && <p className="text-xs text-[#8A93A6]">No outro tracks available</p>}
                  </div>
                </div>
                {/* SFX loadout */}
                <div>
                  <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Sound Effects (Manual)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {allSfx.map(s => {
                      const on = sfxLoadout.find(x => x.name === s.name);
                      return (
                        <Button key={s.name} size="sm" variant={on ? 'default' : 'outline'} onClick={() => toggleSfx(s)} className={`text-xs ${on ? 'bg-cyan-600' : 'border-white/10'}`} data-testid={`sfx-${s.name}`}>
                          <Zap className="h-3 w-3 mr-1" />{s.label}
                        </Button>
                      );
                    })}
                    {allSfx.length === 0 && <p className="text-xs text-[#8A93A6]">No SFX available</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Go Live Presets */}
      {b2Ok && (
        <Card className="glass">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Radio className="h-4 w-4 text-[#3A7BFF]" />Go Live Presets</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[380px]" data-testid="golive-presets-panel-wrapper">
              <EditorTemplatesPanel
                creatorId={creatorId} templateKind="Go Live Preset"
                buildTemplatePayload={buildGoLivePresetPayload} onApply={applyGoLivePreset}
                applyHint="Set up your alert sounds, intro/outro and platforms above, then save them here as a reusable preset."
                projectId={projectId}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Monitoring Panel (memoized) ──────────────────────────
const LiveMonitoring = memo(function LiveMonitoring({ session, isConnected, participants, startTime, markers, events, platforms }) {
  const evtIcon = useCallback((type) => {
    if (type === 'warning') return <AlertTriangle className="h-3 w-3 text-yellow-400 flex-shrink-0" />;
    if (type === 'error') return <XCircle className="h-3 w-3 text-red-400 flex-shrink-0" />;
    if (type === 'sound') return <Volume2 className="h-3 w-3 text-cyan-400 flex-shrink-0" />;
    if (type === 'chat') return <MessageSquare className="h-3 w-3 text-green-400 flex-shrink-0" />;
    return <Info className="h-3 w-3 text-blue-400 flex-shrink-0" />;
  }, []);

  return (
    <Card className="glass" data-testid="live-monitoring">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-green-400" />Monitoring</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {session && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-3 space-y-1">
            <p className="text-sm text-white font-medium truncate">{session.room_name}</p>
            {session.creator_name && <p className="text-xs text-[#8A93A6]">Creator: {session.creator_name}</p>}
            {session.project_name && <p className="text-xs text-[#8A93A6]">Project: {session.project_name}</p>}
            {platforms?.length > 0 && (
              <div className="flex gap-1 mt-1">{platforms.map(p => <PlatBadge key={p} platform={p} />)}</div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-white/5 border border-white/10 p-2">
            <p className="text-[10px] text-[#8A93A6]">Connection</p>
            <div className="flex items-center gap-1"><Wifi className={`h-3 w-3 ${isConnected ? 'text-green-400' : 'text-red-400'}`} /><span className={`text-xs font-medium ${isConnected ? 'text-green-400' : 'text-red-400'}`}>{isConnected ? 'OK' : 'Down'}</span></div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-2">
            <p className="text-[10px] text-[#8A93A6]">Recording</p>
            <div className="flex items-center gap-1"><HardDrive className={`h-3 w-3 ${session?.recording_status === 'recording' ? 'text-red-400 animate-pulse' : 'text-[#8A93A6]'}`} /><span className={`text-xs font-medium capitalize ${session?.recording_status === 'recording' ? 'text-red-400' : 'text-[#8A93A6]'}`}>{session?.recording_status || 'off'}</span></div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-2">
            <p className="text-[10px] text-[#8A93A6]">Elapsed</p>
            <LiveTimer startTime={startTime} />
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-2">
            <p className="text-[10px] text-[#8A93A6]">Participants</p>
            <div className="flex items-center gap-1"><Users className="h-3 w-3 text-[#3A7BFF]" /><span className="text-xs font-medium text-white">{participants}</span></div>
          </div>
        </div>
        {markers.length > 0 && (
          <div><p className="text-[10px] text-[#8A93A6] uppercase tracking-wider mb-1">Markers ({markers.length})</p>
            <div className="space-y-0.5 max-h-20 overflow-y-auto">{markers.map((m, i) => <div key={i} className="flex items-center gap-1 text-[11px]"><Flag className="h-3 w-3 text-yellow-400 flex-shrink-0" /><span className="text-[#8A93A6] font-mono">{m.time}</span><span className="text-white truncate">{m.label}</span></div>)}</div>
          </div>
        )}
        {events.length > 0 && (
          <div><p className="text-[10px] text-[#8A93A6] uppercase tracking-wider mb-1">Events</p>
            <div className="space-y-0.5 max-h-28 overflow-y-auto">{events.slice(-12).reverse().map((e, i) => <div key={i} className="flex items-center gap-1 text-[11px]">{evtIcon(e.type)}<span className="text-[#8A93A6] font-mono">{e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : ''}</span><span className="text-white truncate">{e.message}</span></div>)}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// ─── Unified Chat Feed (memoized) ─────────────────────────
const LiveChat = memo(function LiveChat({ sessionId, onChatEvent, demoMode }) {
  const [messages, setMessages] = useState([]);
  const [sinceTs, setSinceTs] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!sessionId || demoMode) return;
    const poll = async () => {
      try {
        const url = sinceTs ? `${API}/api/live-sessions/${sessionId}/chat?since=${sinceTs}` : `${API}/api/live-sessions/${sessionId}/chat`;
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          const msgs = await res.json();
          if (msgs.length > 0) {
            setMessages(prev => [...prev, ...msgs]);
            setSinceTs(msgs[msgs.length - 1].timestamp);
            msgs.forEach(m => { if (m.event_type !== 'message') onChatEvent?.(m); });
          }
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [sessionId, sinceTs, onChatEvent, demoMode]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const EvtIcon = useCallback(({ type }) => {
    const I = EVT_ICONS[type] || MessageSquare;
    return <I className="h-3 w-3 flex-shrink-0" />;
  }, []);

  return (
    <Card className="glass" data-testid="live-chat">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4 text-green-400" />Live Chat</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
          {messages.length === 0 && (
            <p className="text-xs text-[#8A93A6] text-center py-4">
              {demoMode ? 'Demo Mode — chat is not connected to any real platform.' : 'Chat messages from connected platforms will appear here'}
            </p>
          )}
          {messages.map(m => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <PlatBadge platform={m.platform} />
              <EvtIcon type={m.event_type} />
              <div className="min-w-0">
                <span className="text-[#3A7BFF] font-medium">{m.author}</span>
                <span className="text-white ml-1">{m.message}</span>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </CardContent>
    </Card>
  );
});

// ─── Active Sound Controls ────────────────────────────────
const ActiveSoundControls = memo(function ActiveSoundControls({ soundConfig, onSoundEvent }) {
  const [playing, setPlaying] = useState(null);
  const intro = soundConfig?.intro;
  const outro = soundConfig?.outro;
  const sfx = soundConfig?.sfx_loadout || [];

  const trigger = useCallback((sound, label) => {
    const url = getSoundUrl(sound);
    if (!url) return;
    if (playing === sound.name) { stopAudio(url); setPlaying(null); return; }
    if (playing) stopAudio(getSoundUrl(sfx.find(s => s.name === playing) || intro || outro));
    const a = playAudio(url);
    setPlaying(sound.name);
    a.onended = () => setPlaying(null);
    onSoundEvent?.('sound', `${label}: ${sound.label}`);
  }, [playing, sfx, intro, outro, onSoundEvent]);

  return (
    <Card className="glass" data-testid="active-sound-controls">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Volume2 className="h-4 w-4 text-[#3A7BFF]" />Sound Controls</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {/* Auto alerts status */}
        <div>
          <p className="text-[10px] text-[#8A93A6] uppercase tracking-wider mb-1">Alerts (Auto)</p>
          <div className="flex gap-1 flex-wrap">
            {(soundConfig?.alert_mappings || []).filter(a => a.enabled && a.sound_name).map(a => (
              <Badge key={a.event_type} className="bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 text-[10px] capitalize">{a.event_type}</Badge>
            ))}
            {(soundConfig?.alert_mappings || []).filter(a => a.enabled && a.sound_name).length === 0 && <span className="text-xs text-[#8A93A6]">No alerts configured</span>}
          </div>
        </div>
        {/* Manual: intro / outro */}
        <div className="flex gap-2">
          {intro && (
            <Button size="sm" variant={playing === intro.name ? 'default' : 'outline'} onClick={() => trigger(intro, 'Intro')} className={`text-xs flex-1 ${playing === intro.name ? 'bg-purple-600' : 'border-purple-500/30 text-purple-400'}`} data-testid="play-intro">
              {playing === intro.name ? <Pause className="h-3 w-3 mr-1" /> : <Play className="h-3 w-3 mr-1" />}Intro
            </Button>
          )}
          {outro && (
            <Button size="sm" variant={playing === outro.name ? 'default' : 'outline'} onClick={() => trigger(outro, 'Outro')} className={`text-xs flex-1 ${playing === outro.name ? 'bg-pink-600' : 'border-pink-500/30 text-pink-400'}`} data-testid="play-outro">
              {playing === outro.name ? <Pause className="h-3 w-3 mr-1" /> : <Play className="h-3 w-3 mr-1" />}Outro
            </Button>
          )}
          {!intro && !outro && <span className="text-xs text-[#8A93A6]">No intro/outro selected</span>}
        </div>
        {/* Manual SFX */}
        {sfx.length > 0 && (
          <div>
            <p className="text-[10px] text-[#8A93A6] uppercase tracking-wider mb-1">SFX (Manual)</p>
            <div className="grid grid-cols-2 gap-1.5">
              {sfx.map(s => (
                <Button key={s.name} size="sm" variant={playing === s.name ? 'default' : 'outline'} onClick={() => trigger(s, 'SFX')} className={`text-xs justify-start ${playing === s.name ? 'bg-cyan-600' : 'border-white/10 hover:bg-white/5'}`} data-testid={`sfx-trigger-${s.name}`}>
                  {playing === s.name ? <Pause className="h-3 w-3 mr-1 flex-shrink-0" /> : <Zap className="h-3 w-3 mr-1 flex-shrink-0" />}
                  <span className="truncate">{s.label}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        {sfx.length === 0 && <p className="text-xs text-[#8A93A6]">No SFX in loadout</p>}
      </CardContent>
    </Card>
  );
});

// ─── Active Live Session ──────────────────────────────────
function ActiveLiveSession({ token, serverUrl, roomName, sessionId, soundConfig, platforms, onEnd, demoMode }) {
  const [isConnected, setIsConnected] = useState(false);
  const roomRef = useRef(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [participantCount, setParticipantCount] = useState(demoMode ? 1 : 0);
  const [markers, setMarkers] = useState([]);
  const [events, setEvents] = useState([{ type: 'info', message: demoMode ? 'Demo session started (simulated)' : 'Session started', timestamp: new Date().toISOString() }]);
  const [sessionData, setSessionData] = useState(null);
  const startTimeRef = useRef(Date.now());
  const elapsedRef = useRef(0);
  const videoRef = useRef(null);

  // Timer for elapsed ref (no re-renders)
  useEffect(() => {
    const iv = setInterval(() => { elapsedRef.current = Math.floor((Date.now() - startTimeRef.current) / 1000); }, 1000);
    return () => clearInterval(iv);
  }, []);

  // Session polling (10s, reduced from 5s) — skipped in Demo Mode since
  // there is no real live-session record to poll.
  useEffect(() => {
    if (!sessionId || demoMode) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API}/api/live-sessions/${sessionId}`, { credentials: 'include' });
        if (res.ok && !cancelled) setSessionData(await res.json());
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [sessionId, demoMode]);

  // Start recording once — preserved for Level 3, intentionally not
  // invoked in Demo Mode so no real recording job is ever started.
  useEffect(() => {
    if (!sessionId || demoMode) return;
    fetch(`${API}/api/live-sessions/${sessionId}/start-recording`, { method: 'POST', credentials: 'include' })
      .then(r => r.ok && r.json()).then(d => { if (d) addEventLocal('info', `Recording started (${d.mode})`); })
      .catch(e => addEventLocal('warning', `Recording failed: ${e.message}`));
  }, [sessionId, demoMode]); // eslint-disable-line

  // LiveKit connect — preserved for Level 3, intentionally not invoked in
  // Demo Mode so no real LiveKit session is ever created or joined.
  useEffect(() => {
    if (demoMode || !token || !serverUrl) return;
    let lkRoom = null;
    (async () => {
      try {
        const { Room, RoomEvent } = await import('livekit-client');
        lkRoom = new Room({ adaptiveStream: true, dynacast: true });
        lkRoom.on(RoomEvent.Connected, () => { setIsConnected(true); addEventLocal('info', 'Connected'); });
        lkRoom.on(RoomEvent.Disconnected, () => { setIsConnected(false); addEventLocal('warning', 'Disconnected'); });
        lkRoom.on(RoomEvent.Reconnecting, () => addEventLocal('warning', 'Reconnecting...'));
        lkRoom.on(RoomEvent.Reconnected, () => addEventLocal('info', 'Reconnected'));
        lkRoom.on(RoomEvent.ParticipantConnected, (p) => { updateParts(lkRoom); addEventLocal('info', `Joined: ${p.identity}`); });
        lkRoom.on(RoomEvent.ParticipantDisconnected, (p) => { updateParts(lkRoom); addEventLocal('info', `Left: ${p.identity}`); });
        lkRoom.on(RoomEvent.TrackSubscribed, (track) => { if (track.kind === 'video' && videoRef.current) track.attach(videoRef.current); });
        await lkRoom.connect(serverUrl, token);
        roomRef.current = lkRoom;
        updateParts(lkRoom);
        await lkRoom.localParticipant.enableCameraAndMicrophone();
        const vt = lkRoom.localParticipant.getTrackPublication('camera');
        if (vt?.track && videoRef.current) vt.track.attach(videoRef.current);
      } catch (e) { addEventLocal('error', `Connect failed: ${e.message}`); toast.error(e.message); }
    })();
    return () => { if (lkRoom) lkRoom.disconnect(); };
  }, [token, serverUrl, demoMode]); // eslint-disable-line

  const updateParts = useCallback((r) => {
    if (!r) return;
    setParticipantCount(1 + r.remoteParticipants.size);
  }, []);

  const addEventLocal = useCallback((type, message) => {
    const evt = { type, message, timestamp: new Date().toISOString() };
    setEvents(prev => [...prev, evt]);
    if (sessionId && !demoMode) fetch(`${API}/api/live-sessions/${sessionId}/events`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, message }) }).catch(() => {});
  }, [sessionId, demoMode]);

  const handleChatEvent = useCallback((msg) => {
    // Auto-trigger alert sounds for chat events
    if (!soundConfig?.alert_mappings) return;
    const mapping = soundConfig.alert_mappings.find(a => a.enabled && a.event_type === msg.event_type && a.sound_url);
    if (mapping) {
      playAudio(mapping.sound_url);
      addEventLocal('sound', `Alert: ${msg.event_type} from ${msg.author}`);
    }
  }, [soundConfig, addEventLocal]);

  const handleSoundEvent = useCallback((type, message) => {
    addEventLocal('sound', message);
  }, [addEventLocal]);

  const toggleVideo = useCallback(async () => { if (!roomRef.current) { setVideoEnabled(v => !v); return; } await roomRef.current.localParticipant.setCameraEnabled(!videoEnabled); setVideoEnabled(v => !v); }, [videoEnabled]);
  const toggleAudio = useCallback(async () => { if (!roomRef.current) { setAudioEnabled(v => !v); return; } await roomRef.current.localParticipant.setMicrophoneEnabled(!audioEnabled); setAudioEnabled(v => !v); }, [audioEnabled]);
  const shareScreen = useCallback(async () => {
    if (!roomRef.current) { toast.info('Demo Mode: screen share is simulated — not actually shared.'); return; }
    try { await roomRef.current.localParticipant.setScreenShareEnabled(true); addEventLocal('info', 'Screen share started'); }
    catch { addEventLocal('info', 'Screen share cancelled'); }
  }, [addEventLocal]);

  const addMarker = useCallback(() => {
    const sec = elapsedRef.current;
    const t = `${String(Math.floor(sec/3600)).padStart(2,'0')}:${String(Math.floor((sec%3600)/60)).padStart(2,'0')}:${String(sec%60).padStart(2,'0')}`;
    const m = { time: t, label: `Marker ${markers.length + 1}`, elapsed_seconds: sec };
    setMarkers(prev => [...prev, m]);
    toast.success(`Marker at ${t}`);
    if (sessionId && !demoMode) fetch(`${API}/api/live-sessions/${sessionId}/markers`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(m) }).catch(() => {});
  }, [markers.length, sessionId, demoMode]);

  const handleEnd = useCallback(async () => {
    if (!window.confirm(demoMode ? 'End the demo session?' : 'End live session? Recording saved as replay.')) return;
    addEventLocal('info', 'Ending session...');
    if (roomRef.current) roomRef.current.disconnect();
    const sec = elapsedRef.current;
    const dur = `${String(Math.floor(sec/3600)).padStart(2,'0')}:${String(Math.floor((sec%3600)/60)).padStart(2,'0')}:${String(sec%60).padStart(2,'0')}`;
    if (demoMode) {
      onEnd({ duration: dur, markers, events, roomName, replay_asset_id: null, demo: true });
      return;
    }
    try {
      const res = await fetch(`${API}/api/live-sessions/${sessionId}/end`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ duration: dur, markers, events }) });
      if (res.ok) { const r = await res.json(); onEnd({ ...r, duration: dur, markers, events, roomName }); }
      else throw new Error('Failed');
    } catch (e) { toast.error(e.message); onEnd({ duration: dur, markers, events, roomName, replay_asset_id: null }); }
  }, [sessionId, markers, events, onEnd, roomName, addEventLocal, demoMode]);

  const statusLive = !demoMode && isConnected;

  return (
    <div className="space-y-4" data-testid="active-live-session">
      {/* Status bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {demoMode ? (
            <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/30" data-testid="demo-mode-status-badge">
              <Radio className="h-3 w-3 mr-1" />DEMO MODE
            </Badge>
          ) : (
            <Badge className={statusLive ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}>
              <Radio className={`h-3 w-3 mr-1 ${statusLive ? 'animate-pulse' : ''}`} />{statusLive ? 'LIVE' : 'Connecting...'}
            </Badge>
          )}
          <span className="text-sm text-[#8A93A6]">{roomName}</span>
          <Badge variant="outline" className="text-[#8A93A6]"><Users className="h-3 w-3 mr-1" />{participantCount}</Badge>
          <Badge className={`${sessionData?.recording_status === 'recording' ? 'bg-red-500/10 text-red-400 border-red-500/30' : 'bg-white/5 text-[#8A93A6] border-white/10'}`} data-testid="recording-badge">
            <HardDrive className={`h-3 w-3 mr-1 ${sessionData?.recording_status === 'recording' ? 'animate-pulse' : ''}`} />REC {sessionData?.recording_status === 'recording' ? 'ON' : 'OFF'}
          </Badge>
          {platforms?.length > 0 && platforms.map(p => <PlatBadge key={p} platform={p} />)}
        </div>
        <Badge className="bg-white/5 border-white/10 text-white text-sm" data-testid="live-timer">
          <Clock className="h-3 w-3 mr-1 text-red-400" /><LiveTimer startTime={startTimeRef.current} />
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Video */}
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-white/10">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {demoMode ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60"><p className="text-sm text-[#8A93A6]">Demo Mode — no real video feed</p></div>
            ) : (
              !statusLive && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-8 w-8 text-white animate-spin" /></div>
            )}
            {sessionData?.recording_status === 'recording' && <div className="absolute top-3 left-3 flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" /><span className="text-xs text-white bg-black/50 px-2 py-0.5 rounded">REC</span></div>}
          </div>
          {/* Controls */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button size="sm" variant={audioEnabled ? 'default' : 'destructive'} onClick={toggleAudio} className="h-10 w-10 p-0" data-testid="toggle-audio">{audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</Button>
            <Button size="sm" variant={videoEnabled ? 'default' : 'destructive'} onClick={toggleVideo} className="h-10 w-10 p-0" data-testid="toggle-video">{videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}</Button>
            <Button size="sm" variant="outline" onClick={shareScreen} className="h-10 w-10 p-0 border-white/10" data-testid="share-screen"><MonitorUp className="h-4 w-4" /></Button>
            <div className="w-px h-8 bg-white/10" />
            <Button size="sm" variant="outline" onClick={addMarker} className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10" data-testid="add-marker-btn"><Flag className="h-4 w-4 mr-1" />Marker</Button>
            <div className="w-px h-8 bg-white/10" />
            <Button size="sm" variant="destructive" onClick={handleEnd} data-testid="end-session-btn"><Square className="h-4 w-4 mr-1" />End</Button>
          </div>
          {/* Chat panel */}
          <LiveChat sessionId={sessionId} onChatEvent={handleChatEvent} demoMode={demoMode} />
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <LiveMonitoring session={sessionData} isConnected={statusLive} participants={participantCount} startTime={startTimeRef.current} markers={markers} events={events} platforms={platforms} />
          <ActiveSoundControls soundConfig={soundConfig} onSoundEvent={handleSoundEvent} />
        </div>
      </div>
    </div>
  );
}

// ─── Post-Live Summary (unchanged) ────────────────────────
function PostLiveSummary({ sessionResult, onDone, projectId }) {
  const navigate = useNavigate();
  const replayId = sessionResult?.replay_asset_id;
  const [replayStatus, setReplayStatus] = useState('processing');

  useEffect(() => {
    if (!replayId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API}/api/admin/content-sources`, { credentials: 'include' });
        if (res.ok) {
          const sources = await res.json();
          const replay = (Array.isArray(sources) ? sources : []).find(s => s.id === replayId);
          if (replay && !cancelled) setReplayStatus(replay.status || 'processing');
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [replayId]);

  return (
    <div className="max-w-xl mx-auto" data-testid="post-live-summary">
      <Card className="glass">
        <CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-400" />Session Complete</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {sessionResult.demo && (
            <div className="rounded-lg bg-violet-500/10 border border-violet-500/25 px-3 py-2 text-xs text-violet-300" data-testid="demo-session-notice">
              Demo Mode: this was a simulated session. No real broadcast, recording, or replay was created.
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-white/5 border border-white/10 p-3"><p className="text-xs text-[#8A93A6]">Room</p><p className="text-white text-sm font-medium">{sessionResult.roomName}</p></div>
            <div className="rounded-lg bg-white/5 border border-white/10 p-3"><p className="text-xs text-[#8A93A6]">Duration</p><p className="text-white text-sm font-medium font-mono">{sessionResult.duration}</p></div>
          </div>
          {!sessionResult.demo && (
          <div className="rounded-lg border p-4 space-y-2" style={{ borderColor: replayStatus === 'processing' ? 'rgba(59,130,246,0.3)' : replayStatus === 'completed' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)', background: 'rgba(255,255,255,0.02)' }} data-testid="replay-status">
            <div className="flex items-center gap-2">
              {replayStatus === 'processing' && <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />}
              {replayStatus === 'completed' && <CheckCircle className="h-4 w-4 text-green-400" />}
              {replayStatus === 'failed' && <XCircle className="h-4 w-4 text-red-400" />}
              <p className="text-sm text-white font-medium">Replay: <span className="capitalize">{replayStatus}</span></p>
            </div>
            {replayId && <p className="text-xs text-[#8A93A6]">Asset: {replayId.slice(0, 12)}...</p>}
          </div>
          )}
          {sessionResult.markers?.length > 0 && (
            <div><p className="text-xs text-[#8A93A6] uppercase tracking-wider mb-2">Markers ({sessionResult.markers.length})</p>
              <div className="space-y-1">{sessionResult.markers.map((m, i) => <div key={i} className="flex items-center gap-2 text-sm rounded-lg bg-white/5 px-3 py-2"><Flag className="h-3 w-3 text-yellow-400" /><span className="text-[#8A93A6] font-mono text-xs">{m.time}</span><span className="text-white">{m.label}</span></div>)}</div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            {replayId && <Button onClick={() => navigate(`/app/create/workspace/project/${projectId}`)} className="flex-1 bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" data-testid="go-to-assets-btn"><ArrowRight className="h-4 w-4 mr-2" />View in Project</Button>}
            <Button variant="outline" onClick={onDone} className="border-white/10 flex-1" data-testid="done-live-btn">New Session</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────
function LivePageInner() {
  const { projectId } = useParams();
  const creatorId = localStorage.getItem('activeCreatorId');
  const [projectName, setProjectName] = useState(null);
  const [lkStatus, setLkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [stage, setStage] = useState('pre');
  const [session, setSession] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [postResult, setPostResult] = useState(null);
  const [soundCatalog, setSoundCatalog] = useState({});
  const [soundConfig, setSoundConfig] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        // Reuses the same existing project-read endpoint as Project
        // Overview — no new backend operation is introduced.
        const reqs = [
          fetch(`${API}/api/livekit/status`, { credentials: 'include' }),
          fetch(`${API}/api/sound-assets`, { credentials: 'include' }),
        ];
        if (projectId) reqs.push(fetch(`${API}/api/projects/${projectId}`, { credentials: 'include' }));
        const [lk, snd, proj] = await Promise.all(reqs);
        if (lk.ok) setLkStatus(await lk.json());
        if (snd.ok) setSoundCatalog(await snd.json());
        if (proj && proj.ok) setProjectName((await proj.json())?.name || null);
      } catch {} finally { setLoading(false); }
    })();
  }, [projectId]);

  // DEMO MODE (Level 2 UI recovery) — intentionally does NOT call the real
  // /api/livekit/token or /api/live-sessions endpoints, so no real LiveKit
  // session or broadcast is ever created here. That code path is preserved
  // in ActiveLiveSession (guarded by `demoMode`) for Level 3.
  const handleGoLive = useCallback((roomName, platforms, config) => {
    setJoining(true);
    setSession({ token: null, url: null, roomName });
    setSessionId(`demo-${Date.now()}`);
    setSoundConfig(config);
    setSelectedPlatforms(platforms || []);
    setStage('active');
    setJoining(false);
    toast.info('Demo Mode: simulated session — no real broadcast was started.');
  }, []);

  const handleEnd = useCallback((result) => { setPostResult(result); setStage('post'); setSession(null); }, []);
  const handleDone = useCallback(() => { setStage('pre'); setPostResult(null); setSessionId(null); }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 text-[#3A7BFF] animate-spin" /></div>;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
        <Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span>/</span>
        <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white">{projectName || 'Project'}</Link><span>/</span>
        <span className="text-white">Live</span>
      </div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {projectId && <><span className="text-xs text-[#8A93A6]">Project:</span><span className="text-xs text-[#3A7BFF] font-medium">{projectName || projectId}</span></>}
        <Badge variant="outline" className="border-violet-500/30 text-violet-300 bg-violet-500/10 text-[10px]" data-testid="live-demo-mode-badge">Demo Mode — simulated session</Badge>
        {!lkStatus?.configured && (
          <Badge variant="outline" className="border-amber-500/30 text-amber-300 bg-amber-500/10 text-[10px]" data-testid="livekit-not-configured-badge">LiveKit not configured (not required in Demo Mode)</Badge>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h1 className="text-3xl font-bold text-white">Live</h1>
        <ProductionToolsDropdown currentTool="live" creatorId={creatorId} projectId={projectId} returnTo={`/app/create/workspace/project/${projectId}/live`} />
      </div>
      <p className="text-[#8A93A6] mb-6">
        {stage === 'pre' && 'Configure devices, platforms, and sounds — then go live'}
        {stage === 'active' && 'Demo Mode — monitor, mark, and control a simulated session'}
        {stage === 'post' && 'Session ended — review your replay'}
      </p>

      {stage === 'pre' && <PreLiveSetup onGoLive={handleGoLive} loading={joining} soundCatalog={soundCatalog} creatorId={creatorId} projectId={projectId} />}
      {stage === 'active' && session && <ActiveLiveSession token={session.token} serverUrl={session.url} roomName={session.roomName} sessionId={sessionId} soundConfig={soundConfig} platforms={selectedPlatforms} onEnd={handleEnd} demoMode />}
      {stage === 'post' && postResult && <PostLiveSummary sessionResult={postResult} onDone={handleDone} projectId={projectId} />}
    </div>
  );
}

export default function LivePage() {
  return <LivePageInner />;
}